import {
  computePageGeometry,
  mergeTheme,
  resolveValue,
  paginate,
  resolveTemplate,
  renderNode,
  h,
  walkVNodes,
  type BlockRegistry,
  type DataSource,
  type GeometryMap,
  type Measurer,
  type PagedDocument,
  type PageGeometry,
  type ReportData,
  type ReportTemplate,
  type RenderMode,
  type RenderTreeOptions,
  type ResolvedDocument,
  type ResolvedNode,
  type SectionGeometry,
  type TableRowGeometry,
  type Theme,
  type VNode,
  type VProps,
  type FormatContext,
  type LayoutWarning
} from "@broadpaper/core";

export interface LayoutInput {
  template: ReportTemplate;
  registry: BlockRegistry;
  theme?: Partial<Theme>;
  dataSources?: DataSource[];
  data?: ReportData;
  sampleData?: ReportData;
  mode?: RenderMode;
  now?: Date;
  locale?: string;
  currency?: string;
  timeZone?: string;
  designIterations?: number;
  allowStyleOverrides?: boolean;
  /** Minimum pages to show (design canvas). */
  minPages?: number;
  /** Editor hook to decorate node wrappers. */
  decorate?: RenderTreeOptions["decorate"];
}

export interface LayoutResult {
  resolved: ResolvedDocument;
  paged: PagedDocument;
  geometry: GeometryMap;
  regionGeometry: GeometryMap;
  page: PageGeometry;
  theme: Theme;
  format: FormatContext;
  mode: RenderMode;
  warnings: Array<{ source: "resolve" | "layout"; message: string; nodeId?: string; page?: number; code?: LayoutWarning["code"] }>;
  /** Map of resolved node key → node, across all regions. */
  nodesByKey: Map<string, ResolvedNode>;
  /** Renders one page to a VNode. */
  renderPage(index: number): VNode;
  /** Renders every page. */
  renderPages(): VNode[];
}

/**
 * A measurer whose results arrive asynchronously.
 *
 * The DOM measurer answers immediately because the browser has already done
 * the layout; an engine-backed measurer has to run the engine first. Both
 * drive the identical pipeline — only the awaiting differs.
 */
export interface AsyncMeasurer {
  measure(sections: ResolvedNode[], width: number): Promise<GeometryMap>;
  setOptions?(opts: RenderTreeOptions): void;
}

interface Prepared {
  input: LayoutInput;
  mode: RenderMode;
  theme: Theme;
  template: ReportTemplate;
  page: PageGeometry;
  format: FormatContext;
  resolved: ResolvedDocument;
  baseOpts: RenderTreeOptions;
  regionSections: ResolvedNode[];
}

function indexNodes(doc: ResolvedDocument): Map<string, ResolvedNode> {
  const map = new Map<string, ResolvedNode>();
  const visit = (n: ResolvedNode) => {
    map.set(n.key, n);
    n.children.forEach(visit);
  };
  for (const s of doc.body) visit(s);
  for (const s of doc.header.default) visit(s);
  for (const s of doc.header.firstPage ?? []) visit(s);
  for (const s of doc.footer.default) visit(s);
  for (const s of doc.footer.firstPage ?? []) visit(s);
  return map;
}

/**
 * The full pipeline: resolve → measure → paginate. Returns a result that can
 * render individual pages on demand (the editor virtualises them).
 */
/**
 * Everything up to measurement: resolving the template, page geometry and the
 * render options. Split out so the synchronous and asynchronous entry points
 * share one pipeline and can never drift apart.
 */
function prepareLayout(input: LayoutInput): Prepared {
  const mode: RenderMode = input.mode ?? "preview";
  const theme = mergeTheme(input.theme);
  const template = input.template;
  const page = computePageGeometry(template.page);
  const now = input.now ?? new Date();
  const format: FormatContext = {
    locale: input.locale ?? template.settings?.locale ?? "en-GB",
    currency: input.currency ?? template.settings?.currency ?? "GBP",
    timeZone: input.timeZone ?? template.settings?.timeZone,
    now
  };

  const resolved = resolveTemplate({
    template,
    registry: input.registry,
    theme,
    dataSources: input.dataSources,
    data: input.data,
    sampleData: input.sampleData,
    mode,
    now,
    locale: format.locale,
    currency: format.currency,
    timeZone: format.timeZone,
    designIterations: input.designIterations,
    allowStyleOverrides: input.allowStyleOverrides
  });

  const baseOpts: RenderTreeOptions = {
    registry: input.registry,
    theme,
    styles: template.styles,
    mode,
    page: { number: 1, total: 1 },
    format,
    width: page.contentWidth,
    decorate: input.decorate
  };

  const regionSections = [...resolved.header.default, ...(resolved.header.firstPage ?? []), ...resolved.footer.default, ...(resolved.footer.firstPage ?? [])];
  return { input, mode, theme, template, page, format, resolved, baseOpts, regionSections };
}

/** Completes a layout once its geometry has been measured, however that happened. */
function finishLayout(p: Prepared, geometry: GeometryMap, regionGeometry: GeometryMap): LayoutResult {
  const { input, mode, theme, template, page, format, resolved, baseOpts } = p;

  const paged = paginate({ document: resolved, geometry, page, minPages: input.minPages ?? template.page.minPages ?? 1, regionGeometry });
  const nodesByKey = indexNodes(resolved);

  const warnings: LayoutResult["warnings"] = [
    ...resolved.warnings.map((w) => ({ source: "resolve" as const, message: w.message, nodeId: w.nodeId })),
    ...paged.warnings.map((w) => ({ source: "layout" as const, message: w.message, nodeId: w.nodeId, page: w.page, code: w.code }))
  ];

  const renderPage = (index: number): VNode => {
    const p = paged.pages[index];
    if (!p) throw new Error(`Page ${index} does not exist`);
    const opts: RenderTreeOptions = { ...baseOpts, page: { number: p.number, total: paged.totalPages } };
    return composePage(p.index, p.number, paged.totalPages, p.first, resolved, paged, page, opts, nodesByKey, regionGeometry, theme, template.page.background, geometry);
  };

  return {
    resolved,
    paged,
    geometry,
    regionGeometry,
    page,
    theme,
    format,
    mode,
    warnings,
    nodesByKey,
    renderPage,
    renderPages: () => paged.pages.map((_, i) => renderPage(i))
  };
}

/** Lays out a document with a measurer that answers immediately. */
export function layoutDocument(input: LayoutInput, measurer: Measurer & { setOptions?(opts: RenderTreeOptions): void }): LayoutResult {
  const p = prepareLayout(input);
  measurer.setOptions?.(p.baseOpts);
  const geometry = measurer.measure(p.resolved.body, p.page.contentWidth);
  const regionGeometry = p.regionSections.length ? measurer.measure(p.regionSections, p.page.contentWidth) : {};
  return finishLayout(p, geometry, regionGeometry);
}

/**
 * Lays out a document with a measurer that answers asynchronously.
 *
 * This is what lets the editor page a document exactly as the PDF backend
 * will: the same engine measures both, so a break in the canvas is a break in
 * the file. Everything after measurement — pagination, composition, warnings —
 * is the code the synchronous path runs.
 */
export async function layoutDocumentAsync(input: LayoutInput, measurer: AsyncMeasurer): Promise<LayoutResult> {
  const p = prepareLayout(input);
  measurer.setOptions?.(p.baseOpts);
  const geometry = await measurer.measure(p.resolved.body, p.page.contentWidth);
  const regionGeometry = p.regionSections.length ? await measurer.measure(p.regionSections, p.page.contentWidth) : {};
  return finishLayout(p, geometry, regionGeometry);
}

/**
 * Windowed rendering: nodes entirely outside the fragment's clip window are
 * replaced by empty boxes of identical size, and table bodies keep only the rows
 * that intersect the window (leading rows become one spacer row). Layout is
 * unchanged; per-page DOM cost becomes proportional to what is visible.
 */
export function windowSection(root: VNode, geom: SectionGeometry | undefined, from: number, to: number): VNode {
  if (!geom) return root;
  const visit = (v: VNode): VNode => {
    const key = v.props["data-key"];
    if (typeof key === "string") {
      const g = geom.nodes[key];
      if (g) {
        const bottom = g.top + g.height;
        if (bottom <= from - 0.01 || g.top >= to + 0.01) {
          const existing = v.props.style;
          const style = typeof existing === "string" ? `${existing};height:${g.height}px;overflow:hidden` : { ...((existing as Record<string, string | number | undefined>) ?? {}), height: `${g.height}px`, overflow: "hidden" };
          return { ...v, props: { ...v.props, style, "data-culled": "" }, children: [] };
        }
        if (g.rows && g.rows.length) return { ...v, children: v.children.map((c) => (typeof c === "string" ? c : windowTable(c, g.rows!, from, to))) };
      }
    }
    return { ...v, children: v.children.map((c) => (typeof c === "string" ? c : visit(c))) };
  };
  return visit(root);
}

function windowTable(v: VNode, rows: TableRowGeometry[], from: number, to: number): VNode {
  if (v.tag === "table") {
    const body = rows.filter((r) => r.kind === "body");
    return {
      ...v,
      children: v.children.map((c) => {
        if (typeof c === "string" || c.tag !== "tbody") return c;
        const trs = c.children.filter((x): x is VNode => typeof x !== "string" && x.tag === "tr");
        if (trs.length !== body.length) return c;
        let leading = 0;
        let trailing = 0;
        const kept: VNode[] = [];
        for (let i = 0; i < trs.length; i++) {
          const r = body[i]!;
          const bottom = r.top + r.height;
          if (bottom <= from + 0.01) leading += r.height;
          else if (r.top >= to - 0.01) trailing += r.height;
          else kept.push(trs[i]!);
        }
        const spacer = (height: number) => h("tr", { class: "bp-table__spacer" }, h("td", { colspan: "100", style: { padding: "0", border: "0", height: `${height}px` } }));
        return { ...c, children: [...(leading > 0 ? [spacer(leading)] : []), ...kept, ...(trailing > 0 ? [spacer(trailing)] : [])] };
      })
    };
  }
  return { ...v, children: v.children.map((c) => (typeof c === "string" ? c : windowTable(c, rows, from, to))) };
}

/** Renders only the header part of a table node (for repeated headers). */
function renderTableHeader(node: ResolvedNode, opts: RenderTreeOptions, width: number): VNode | null {
  const full = renderNode(node, opts, width);
  if (!full) return null;
  walkVNodes(full, (v) => {
    if (v.tag === "table") {
      v.children = v.children.filter((c) => typeof c === "string" || c.tag === "colgroup" || c.tag === "thead");
      return false;
    }
    return true;
  });
  // Strip wrapper margins so the header sits flush at the fragment top.
  const props = { ...full.props } as VProps;
  delete props.style;
  full.props = { ...props, class: [String(props.class ?? ""), "bp-repeat-header__node"] };
  return full;
}

export function composePage(
  index: number,
  number: number,
  total: number,
  first: boolean,
  resolved: ResolvedDocument,
  paged: PagedDocument,
  page: PageGeometry,
  opts: RenderTreeOptions,
  nodesByKey: Map<string, ResolvedNode>,
  regionGeometry: GeometryMap,
  theme: Theme,
  pageBackground?: string,
  geometry?: GeometryMap
): VNode {
  const p = paged.pages[index]!;
  const pageStyle: Record<string, string> = {
    width: `${page.width}px`,
    height: `${page.height}px`,
    background: resolveValue(pageBackground, theme, "#ffffff")
  };

  const region = (sections: ResolvedNode[] | undefined, top: number, height: number, name: "header" | "footer", align: "top" | "bottom") => {
    if (!sections || sections.length === 0 || height <= 0) return null;
    const total = sections.reduce((a, s) => a + (regionGeometry[s.key]?.height ?? 0), 0);
    const offset = align === "bottom" ? Math.max(0, height - total) : 0;
    return h(
      "div",
      { class: ["bp-page__region", `bp-page__${name}`], "data-region": name, style: { top: `${top}px`, left: `${page.margins.left}px`, width: `${page.contentWidth}px`, height: `${height}px` } },
      h("div", { class: "bp-page__region-inner", style: { position: "absolute", top: `${offset}px`, left: "0", width: "100%" } }, sections.map((s) => renderNode(s, opts, page.contentWidth)))
    );
  };

  const headerSections = first && resolved.header.firstPage ? resolved.header.firstPage : resolved.header.default;
  const footerSections = first && resolved.footer.firstPage ? resolved.footer.firstPage : resolved.footer.default;

  const windowed = (section: ResolvedNode, from: number, to: number): VNode | null => {
    const v = renderNode(section, opts, page.contentWidth);
    if (!v) return null;
    return windowSection(v, geometry?.[section.key], from, to);
  };

  const fragments = p.fragments.map((f) => {
    const section = nodesByKey.get(f.sectionKey);
    if (!section) return null;
    const extra = f.repeatHeaders.reduce((a, r) => a + r.height, 0);
    const headers = f.repeatHeaders.map((r) => {
      const t = nodesByKey.get(r.tableKey);
      const v = t ? renderTableHeader(t, opts, r.width) : null;
      return h("div", { class: "bp-repeat-header", "data-table": r.tableKey, style: { left: `${r.left}px`, width: `${r.width}px`, height: `${r.height}px` } }, v);
    });
    return h(
      "div",
      { class: ["bp-fragment", f.continued && "bp-fragment--continued", f.continues && "bp-fragment--continues"], "data-section": f.sectionKey, "data-from": String(f.from), "data-to": String(f.to), style: { top: `${f.y}px`, height: `${f.height}px` } },
      headers,
      h("div", { class: "bp-fragment__clip", style: { top: `${extra}px`, height: `${f.to - f.from}px` } }, h("div", { class: "bp-fragment__content", style: { "margin-top": `${-f.from}px`, width: `${page.contentWidth}px` } }, windowed(section, f.from, f.to)))
    );
  });

  return h(
    "div",
    { class: ["bp-page", first && "bp-page--first"], "data-page": String(number), "data-page-index": String(index), style: pageStyle, "aria-label": `Page ${number} of ${total}` },
    region(headerSections, page.headerTop, page.headerHeight, "header", "top"),
    h("div", { class: "bp-page__body", "data-region": "body", style: { top: `${page.contentTop}px`, left: `${page.margins.left}px`, width: `${page.contentWidth}px`, height: `${page.contentHeight}px` } }, fragments),
    region(footerSections, page.footerTop, page.footerHeight, "footer", "bottom")
  );
}
